import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import {
  browseProducts,
  flattenStorefrontCategories,
  formatMoney,
  percentOff,
  shopPrice,
  listStorefrontCategories,
  type StorefrontFilters,
} from '@/features/marketplace/api'
import { ProductImage } from '@/routes/storefront/ProductImage'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * The public catalog browse page (M24-02).
 *
 * Same search-goes-to-the-server shape as `Products.tsx`: the catalog is thousands of
 * lines, so a text box and a category `<select>` both round-trip rather than filtering
 * whatever page happens to be on screen. There is no status filter here -- the server
 * already hard-codes `status = 'active', listed_on_marketplace = True` and a shopper has
 * no reason to ask for anything else.
 */
export function Storefront() {
  const [filters, setFilters] = useState<StorefrontFilters>({ page: 1 })
  const [search, setSearch] = useState('')

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['storefront-products', filters],
    queryFn: () => browseProducts(filters),
    retry: false,
  })
  const categories = useQuery({
    queryKey: ['storefront-categories'],
    queryFn: listStorefrontCategories,
    retry: false,
    staleTime: 5 * 60_000,
  })

  const products = data?.data ?? []
  const meta = data?.meta
  const options = flattenStorefrontCategories(categories.data ?? [])

  const set = (patch: Partial<StorefrontFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Shop
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {meta ? `${meta.total} product${meta.total === 1 ? '' : 's'}` : 'Loading…'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="store-search">
          Search products
        </label>
        <Input
          id="store-search"
          className="w-56"
          placeholder="Search products…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') set({ q: search })
          }}
          onBlur={() => set({ q: search })}
        />

        <label className="sr-only" htmlFor="store-category">
          Category
        </label>
        <select
          id="store-category"
          className={SELECT_CLASS}
          value={filters.category_id ?? ''}
          onChange={(event) => set({ category_id: event.target.value || undefined })}
        >
          <option value="">All categories</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>

        {(filters.q || filters.category_id) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('')
              setFilters({ page: 1 })
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {error && <Alert tone="error" title="Could not load products." />}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-slate-400" />
        </div>
      ) : products.length === 0 ? (
        <Card>
          <CardBody className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
            No products match these filters.
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((product) => (
            <Link key={product.id} to={`/store/${product.id}`} className="group">
              <Card className="h-full transition-shadow group-hover:shadow-md">
                <div className="aspect-square overflow-hidden rounded-t-lg bg-slate-100 dark:bg-slate-800">
                  <ProductImage src={product.images?.[0]} name={product.name} textClass="text-2xl" />
                </div>
                <CardBody className="space-y-1 p-3">
                  {product.category_name && (
                    <p className="text-xs text-slate-400">{product.category_name}</p>
                  )}
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                    {product.name}
                  </p>
                  <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span className="font-semibold text-brand-600 dark:text-brand-400">
                      {formatMoney(shopPrice(product))}
                    </span>
                    {product.sale_price != null && (
                      <>
                        <span className="text-xs text-slate-400 line-through">
                          {formatMoney(product.price)}
                        </span>
                        <span className="text-xs font-medium text-success">
                          {percentOff(product)}% off
                        </span>
                      </>
                    )}
                  </p>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {meta && meta.total_pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={meta.page <= 1}
            onClick={() => setFilters((current) => ({ ...current, page: meta.page - 1 }))}
          >
            Previous
          </Button>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Page {meta.page} of {meta.total_pages}
            {isFetching && '…'}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={meta.page >= meta.total_pages}
            onClick={() => setFilters((current) => ({ ...current, page: meta.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
